<template>
    <div class="intro box p-5">
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div>
                <h2 class="text-2xl font-medium">Schüler aus CSV oder Excel importieren</h2>
                <p class="text-slate-500 mt-2">
                    Prüfen Sie die Daten in der Vorschau, bevor neue Schüler angelegt werden.
                </p>
            </div>
            <button
                v-if="preview || result"
                type="button"
                class="btn btn-outline-secondary mt-4 lg:mt-0"
                @click="reset"
            >
                Andere Datei wählen
            </button>
        </div>

        <div v-if="!preview && !result" class="mt-8">
            <div
                class="border-2 border-dashed rounded-md p-10 text-center transition-colors"
                :class="dragging ? 'border-primary bg-primary/5' : 'border-slate-300'"
                @dragenter.prevent="dragging = true"
                @dragover.prevent="dragging = true"
                @dragleave.prevent="dragging = false"
                @drop.prevent="onDrop"
            >
                <UploadCloudIcon class="w-12 h-12 mx-auto text-primary" />
                <div class="font-medium text-lg mt-4">CSV- oder Excel-Datei hier ablegen</div>
                <div class="text-slate-500 mt-2">CSV (Semikolon), XLSX oder XLS; maximal 5 MB und 5.000 Datenzeilen</div>
                <div class="text-slate-500 text-sm mt-1">Bei Excel-Dateien wird das erste Arbeitsblatt eingelesen.</div>
                <label class="btn btn-primary mt-5 cursor-pointer">
                    Datei auswählen
                    <input
                        ref="fileInput"
                        type="file"
                        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        class="hidden"
                        @change="onFileSelected"
                    />
                </label>
            </div>

            <div v-if="file" class="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-5 p-4 bg-slate-100 rounded-md">
                <div class="flex items-center min-w-0">
                    <FileTextIcon class="w-6 h-6 text-primary flex-none" />
                    <div class="ml-3 min-w-0">
                        <div class="font-medium truncate">{{ file.name }}</div>
                        <div class="text-slate-500 text-sm">{{ formatFileSize(file.size) }}</div>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-primary mt-4 sm:mt-0 sm:ml-4"
                    :disabled="loading"
                    @click="createPreview"
                >
                    <LoadingIcon v-if="loading" icon="oval" class="w-4 h-4 mr-2" />
                    Vorschau erstellen
                </button>
            </div>
        </div>

        <Alert v-if="error" variant="danger" class="mt-5">
            <AlertCircleIcon class="w-5 h-5 mr-2" />
            {{ error }}
        </Alert>

        <template v-if="preview || result">
            <div class="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
                <div class="border rounded-md p-4">
                    <div class="text-slate-500 text-sm">Zeilen gesamt</div>
                    <div class="text-2xl font-medium mt-1">{{ summary.total }}</div>
                </div>
                <div v-if="!result" class="border rounded-md p-4 border-success/40 bg-success/5">
                    <div class="text-slate-500 text-sm">Bereit</div>
                    <div class="text-2xl font-medium text-success mt-1">{{ summary.ready }}</div>
                </div>
                <div v-else class="border rounded-md p-4 border-success/40 bg-success/5">
                    <div class="text-slate-500 text-sm">Importiert</div>
                    <div class="text-2xl font-medium text-success mt-1">{{ summary.imported }}</div>
                </div>
                <div class="border rounded-md p-4">
                    <div class="text-slate-500 text-sm">Übersprungen</div>
                    <div class="text-2xl font-medium mt-1">{{ summary.skipped }}</div>
                </div>
                <div class="border rounded-md p-4" :class="summary.invalid ? 'border-warning/40 bg-warning/5' : ''">
                    <div class="text-slate-500 text-sm">Ungültig</div>
                    <div class="text-2xl font-medium mt-1">{{ summary.invalid }}</div>
                </div>
                <div v-if="result" class="border rounded-md p-4" :class="summary.errors ? 'border-danger/40 bg-danger/5' : ''">
                    <div class="text-slate-500 text-sm">Fehler</div>
                    <div class="text-2xl font-medium mt-1">{{ summary.errors }}</div>
                </div>
            </div>

            <Alert v-if="result" :variant="summary.errors ? 'warning' : 'success'" class="mt-6">
                <CheckCircleIcon v-if="!summary.errors" class="w-5 h-5 mr-2" />
                <AlertTriangleIcon v-else class="w-5 h-5 mr-2" />
                Der Import ist abgeschlossen. Die Ergebnisse jeder Zeile finden Sie in der Tabelle.
            </Alert>

            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6">
                <div>
                    <div class="font-medium">{{ filename }}</div>
                    <div v-if="!result" class="text-slate-500 text-sm mt-1">
                        Die Vorschau bleibt 30 Minuten gültig. Es wurden noch keine Daten gespeichert.
                    </div>
                </div>
                <button
                    v-if="!result"
                    type="button"
                    class="btn btn-primary mt-4 sm:mt-0"
                    :disabled="loading || summary.ready === 0"
                    @click="runImport"
                >
                    <LoadingIcon v-if="loading" icon="oval" class="w-4 h-4 mr-2" />
                    {{ summary.ready }} Datensätze jetzt importieren
                </button>
            </div>

            <div class="overflow-x-auto mt-5 border rounded-md">
                <table class="table table-striped whitespace-nowrap">
                    <thead>
                        <tr>
                            <th>Zeile</th>
                            <th>Status</th>
                            <th>Hinweis</th>
                            <th v-for="column in columns" :key="column.key">{{ column.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.line">
                            <td>{{ row.line }}</td>
                            <td>
                                <span class="px-2 py-1 rounded-full text-xs font-medium" :class="statusClass(row.status)">
                                    {{ statusLabel(row.status) }}
                                </span>
                            </td>
                            <td class="max-w-xs whitespace-normal">{{ row.message }}</td>
                            <td v-for="column in columns" :key="column.key">
                                {{ row.data[column.key] || '–' }}
                            </td>
                        </tr>
                        <tr v-if="rows.length === 0">
                            <td :colspan="columns.length + 3" class="text-center text-slate-500 py-8">
                                Keine Datenzeilen gefunden.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import authHeader from '../../services/auth-header';

export default {
    name: 'CsvImport',

    data() {
        return {
            file: null,
            dragging: false,
            loading: false,
            error: null,
            preview: null,
            result: null,
        };
    },

    computed: {
        rows() {
            return this.result ? this.result.rows : (this.preview ? this.preview.rows : []);
        },
        summary() {
            return this.result ? this.result.summary : this.preview.summary;
        },
        columns() {
            return this.preview ? this.preview.columns : [];
        },
        filename() {
            return this.preview ? this.preview.filename : '';
        },
    },

    methods: {
        onFileSelected(event) {
            this.setFile(event.target.files[0]);
        },

        onDrop(event) {
            this.dragging = false;
            this.setFile(event.dataTransfer.files[0]);
        },

        setFile(file) {
            this.error = null;

            if (!file) {
                this.file = null;
                return;
            }

            const extension = file.name.toLowerCase().split('.').pop();

            if (!['csv', 'xlsx', 'xls'].includes(extension)) {
                this.file = null;
                this.error = 'Bitte wählen Sie eine CSV-, XLSX- oder XLS-Datei aus.';
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                this.file = null;
                this.error = 'Die CSV-Datei darf höchstens 5 MB groß sein.';
                return;
            }

            this.file = file;
        },

        async createPreview() {
            if (!this.file || this.loading) {
                return;
            }

            this.loading = true;
            this.error = null;
            const formData = new FormData();
            formData.append('file', this.file);

            try {
                const response = await axios.post(
                    this.$store.state.config.apiUrl + '/pupils/import/preview',
                    formData,
                    { headers: authHeader() }
                );
                this.preview = response.data;
            } catch (error) {
                this.error = this.errorMessage(error);
            } finally {
                this.loading = false;
            }
        },

        async runImport() {
            if (!this.preview || this.loading) {
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                const response = await axios.post(
                    this.$store.state.config.apiUrl + '/pupils/import',
                    { token: this.preview.token },
                    { headers: authHeader() }
                );
                this.result = response.data;
            } catch (error) {
                this.error = this.errorMessage(error);
            } finally {
                this.loading = false;
            }
        },

        reset() {
            this.file = null;
            this.preview = null;
            this.result = null;
            this.error = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        errorMessage(error) {
            const errors = error.response && error.response.data && error.response.data.errors;
            if (errors) {
                const first = Object.values(errors)[0];
                return Array.isArray(first) ? first[0] : first;
            }
            return error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Die Anfrage konnte nicht verarbeitet werden.';
        },

        formatFileSize(bytes) {
            return bytes < 1024 * 1024
                ? Math.ceil(bytes / 1024) + ' KB'
                : (bytes / 1024 / 1024).toFixed(1) + ' MB';
        },

        statusLabel(status) {
            return {
                ready: 'Bereit',
                imported: 'Importiert',
                existing: 'Vorhanden',
                duplicate: 'Doppelt',
                invalid: 'Ungültig',
                error: 'Fehler',
            }[status] || status;
        },

        statusClass(status) {
            return {
                ready: 'bg-primary/10 text-primary',
                imported: 'bg-success/10 text-success',
                existing: 'bg-slate-200 text-slate-700',
                duplicate: 'bg-warning/10 text-warning',
                invalid: 'bg-danger/10 text-danger',
                error: 'bg-danger/10 text-danger',
            }[status] || 'bg-slate-200';
        },
    },
};
</script>
